import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  ArrowRight, ArrowLeft, CheckCircle, AlertCircle, Loader2, Sparkles,
  Home, FileText, DollarSign, ClipboardCheck, Building2, MapPin, Upload,
  Clock, ChevronDown, ChevronUp, Pencil, Shield, Zap, TrendingUp,
  HelpCircle, Save, Lock, Eye, EyeOff
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { usePageNavigate } from "../hooks/usePageNavigate";
import {
  LuxuryBackground, Card, GoldButton, PageHero,
  inputClassLight as inputClass, selectClassLight as selectClass,
  labelClassLight as labelClass
} from "../components/shared/UIComponents";
import { API } from "../data/constants";

/* ═══════════════════════════════════════════════════════════════
   Constants
   ═══════════════════════════════════════════════════════════════ */

const PROPERTY_TYPES = [
  { value: "sfr", label: "Single Family", icon: Home, desc: "Detached residential" },
  { value: "multi", label: "Multi-Family", icon: Building2, desc: "2-4 unit properties" },
  { value: "condo", label: "Condo / Townhome", icon: Building2, desc: "Attached residential" },
  { value: "commercial", label: "Commercial", icon: Building2, desc: "Retail, office, industrial" },
  { value: "mixed_use", label: "Mixed Use", icon: Building2, desc: "Residential + commercial" },
  { value: "land", label: "Land", icon: MapPin, desc: "Vacant or entitled land" },
];

const EXIT_STRATEGIES = [
  { value: "sell", label: "Fix & Sell", desc: "Renovate and sell for profit" },
  { value: "rent", label: "Hold & Rent", desc: "Stabilize and lease long-term" },
  { value: "refinance", label: "Refinance", desc: "Transition to permanent debt" },
  { value: "construction_completion", label: "Construction Completion", desc: "Complete build then sell or hold" },
];

const EXIT_BY_LOAN_TYPE = {
  fix_flip: ["sell", "rent"],
  dscr: ["rent", "refinance"],
  bridge: ["sell", "refinance", "construction_completion"],
  construction: ["sell", "rent"],
  ground_up: ["sell", "rent"],
};

const LOAN_TYPES = [
  { value: "fix_flip", label: "Fix & Flip", desc: "Short-term acquisition + rehab financing", icon: TrendingUp },
  { value: "bridge", label: "Bridge Financing", desc: "Transitional capital between acquisition and permanent financing", icon: Zap },
  { value: "dscr", label: "DSCR Rental Loans", desc: "Qualify on property cash flow for income-producing rentals", icon: DollarSign },
  { value: "cash_out", label: "Cash-Out Refinance", desc: "Unlock equity from stabilized assets", icon: DollarSign },
  { value: "construction", label: "Construction", desc: "Finance ground-up or major renovation with draw schedules", icon: Building2 },
  { value: "ground_up", label: "Ground Up", desc: "Land + vertical construction financing", icon: Building2 },
  { value: "multifamily", label: "Multifamily", desc: "Dedicated financing for 5+ unit apartment projects", icon: Home },
  { value: "commercial", label: "Commercial", desc: "CRE, mixed-use, and specialty asset financing", icon: Building2 },
];

const ltMap = Object.fromEntries(LOAN_TYPES.map(l => [l.value, l.label]));

const DOC_CATEGORIES = [
  { value: "purchase_agreement", label: "Purchase Agreement" },
  { value: "property_photos", label: "Property Photos" },
  { value: "renovation_plan", label: "Renovation Plan / SOW" },
  { value: "rent_roll", label: "Rent Roll / Financials" },
  { value: "miscellaneous", label: "Other Documents" },
];

const AUTOSAVE_KEY = "imaani_intake_draft";

import { fmtUSD } from "../utils/currency";
const fmt$ = (v) => v ? fmtUSD(v) : "--";

/* ═══════════════════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════════════════ */

function Tooltip({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-block ml-1 align-middle">
      <HelpCircle className="h-3.5 w-3.5 text-[#aaa] hover:text-emerald-600 cursor-help transition-colors"
        onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} />
      {open && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-2.5 rounded-xl bg-[#0A0A0A] text-white text-[11px] leading-relaxed shadow-xl z-50 pointer-events-none">
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-[#0A0A0A]" />
        </span>
      )}
    </span>
  );
}

function FieldGroup({ title, children, className = "" }) {
  return (
    <div className={`space-y-4 ${className}`}>
      {title && <h4 className="text-xs font-bold uppercase tracking-wider text-[#888] pb-1 border-b border-[#f0ebe0]">{title}</h4>}
      {children}
    </div>
  );
}

function OptionalBadge() {
  return <span className="ml-1.5 text-[10px] font-medium text-[#bbb] uppercase tracking-wide">Optional</span>;
}

/* ═══════════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════════ */

export default function ApplyPage() {
  const { user, login } = useAuth();
  const { navigateTo } = usePageNavigate();
  const [searchParams] = useSearchParams();

  // Detect URL pre-fill from calculator
  const urlPrefill = useMemo(() => {
    const address = searchParams.get("address");
    const loan_type = searchParams.get("loan_type");
    const purchase_price = searchParams.get("purchase_price");
    const rehab_budget = searchParams.get("rehab_budget");
    const arv = searchParams.get("arv");
    const credit_range = searchParams.get("credit_range");
    if (address || loan_type || purchase_price) {
      return { address, loan_type, purchase_price, rehab_budget, arv, credit_range };
    }
    return null;
  }, [searchParams]);

  const isLoggedIn = !!user;
  const STEPS = useMemo(() => [
    { label: "Deal Details", icon: DollarSign, desc: "Basic deal information", time: "~30 sec" },
    { label: "Property Info", icon: Building2, desc: "Property details and financials", time: "~2 min" },
    { label: "Borrower Profile", icon: FileText, desc: "Tell us about yourself", time: "~1 min" },
    { label: "Documents", icon: Upload, desc: "Notes and document preparation", time: "~30 sec" },
    { label: isLoggedIn ? "Review & Submit" : "Create Account & Submit", icon: isLoggedIn ? ClipboardCheck : Shield, desc: isLoggedIn ? "Confirm and submit your deal" : "Secure your submission", time: "~30 sec" },
  ], [isLoggedIn]);

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => {
    // Check for URL prefill first
    if (urlPrefill) {
      return {
        loanType: urlPrefill.loan_type || "", propertyAddress: urlPrefill.address || "",
        requestedLoanAmount: "", closingTimeline: "",
        propertyType: "", bedrooms: "", bathrooms: "", sqft: "", yearBuilt: "",
        purchasePrice: urlPrefill.purchase_price || "", rehabBudget: urlPrefill.rehab_budget || "",
        arv: urlPrefill.arv || "", rentEstimate: "", exitStrategy: "",
        fullName: "", email: "", phone: "", state: "TX", entityName: "",
        experienceProjects: "0", ficoEstimate: urlPrefill.credit_range || "680",
        notes: "",
      };
    }
    // Restore draft
    try {
      const saved = localStorage.getItem(AUTOSAVE_KEY);
      if (saved) return JSON.parse(saved);
    } catch {}
    return {
      loanType: "", propertyAddress: "", requestedLoanAmount: "", closingTimeline: "",
      propertyType: "", bedrooms: "", bathrooms: "", sqft: "", yearBuilt: "",
      purchasePrice: "", rehabBudget: "", arv: "", rentEstimate: "", exitStrategy: "",
      fullName: "", email: "", phone: "", state: "TX", entityName: "",
      experienceProjects: "0", ficoEstimate: "680", notes: "",
    };
  });

  // Account creation fields (Step 5 for non-authenticated users)
  const [accountPassword, setAccountPassword] = useState("");
  const [accountConfirm, setAccountConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dealResult, setDealResult] = useState(null);
  const [dealProgress, setDealProgress] = useState(null);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState("purchase_agreement");
  const [showMobileSummary, setShowMobileSummary] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const autoSaveTimer = useRef(null);

  // Prefill from user account if logged in
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("ic_token");
    const loadVault = async () => {
      try {
        const res = await axios.get(`${API}/profile/vault/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const v = res.data?.vault || {};
        setForm(prev => ({
          ...prev,
          fullName: prev.fullName || v.full_name || user.full_name || "",
          email: prev.email || v.email || user.email || "",
          phone: prev.phone || v.phone || user.phone || "",
          entityName: prev.entityName || v.entity_name || "",
          ficoEstimate: prev.ficoEstimate || String(v.credit_score || "680"),
          experienceProjects: prev.experienceProjects || String(v.experience_projects || "0"),
        }));
      } catch {
        setForm(prev => ({
          ...prev,
          fullName: prev.fullName || user.full_name || "",
          email: prev.email || user.email || "",
        }));
      }
    };
    if (!form.fullName && !form.email) loadVault();
  }, [user]);

  // Autosave
  useEffect(() => {
    if (submitted) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      try {
        localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(form));
        setLastSaved(new Date());
      } catch {}
    }, 2000);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [form, submitted]);

  const handleChange = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Conditional field visibility
  const showRehab = ["fix_flip", "construction", "ground_up"].includes(form.loanType);
  const showARV = ["fix_flip", "bridge", "construction", "ground_up"].includes(form.loanType);
  const showRent = ["dscr", "multifamily"].includes(form.loanType);
  const showResidentialFields = !["commercial", "land"].includes(form.propertyType);
  const isLand = form.propertyType === "land";

  const summary = useMemo(() => {
    const purchase = Number(form.purchasePrice) || 0;
    const rehab = Number(form.rehabBudget) || 0;
    const totalCost = purchase + rehab;
    const arv = Number(form.arv) || 0;
    const requested = Number(form.requestedLoanAmount) || Math.round(totalCost * 0.85);
    const ltc = totalCost ? ((requested / totalCost) * 100).toFixed(1) : null;
    const ltv = arv ? ((requested / arv) * 100).toFixed(1) : null;
    return { totalCost, requested, ltc, ltv };
  }, [form.purchasePrice, form.rehabBudget, form.arv, form.requestedLoanAmount]);

  const validatePassword = (pwd) => {
    const errs = [];
    if (pwd.length < 8) errs.push("At least 8 characters");
    if (!/[A-Z]/.test(pwd)) errs.push("One uppercase letter");
    if (!/[a-z]/.test(pwd)) errs.push("One lowercase letter");
    if (!/\d/.test(pwd)) errs.push("One digit");
    return errs;
  };

  const validateStep = (s) => {
    if (s === 1) return form.loanType && form.propertyAddress;
    if (s === 2) return form.propertyType && form.purchasePrice;
    if (s === 3) return form.fullName && form.email && form.phone;
    if (s === 4) return true; // optional
    if (s === 5) {
      if (isLoggedIn) return true;
      const pwdErrs = validatePassword(accountPassword);
      return form.email && accountPassword && accountPassword === accountConfirm && pwdErrs.length === 0;
    }
    return true;
  };

  const nextStep = () => { if (validateStep(step)) setStep(s => Math.min(s + 1, 5)); };
  const prevStep = () => setStep(s => Math.max(s - 1, 1));

  /* ── Build the deal payload (shared between auth & non-auth submit) ── */
  const buildPayload = () => ({
    full_name: form.fullName, email: form.email, phone: form.phone, state: form.state || "TX",
    entity_name: form.entityName || null, experience_projects_completed: parseInt(form.experienceProjects) || 0,
    fico_estimate: parseInt(form.ficoEstimate) || 680, loan_type: form.loanType,
    property_address: form.propertyAddress, property_type: form.propertyType || null,
    bedrooms: form.bedrooms ? parseInt(form.bedrooms) : null,
    bathrooms: form.bathrooms ? parseFloat(form.bathrooms) : null,
    sqft: form.sqft ? parseInt(form.sqft) : null,
    year_built: form.yearBuilt ? parseInt(form.yearBuilt) : null,
    purchase_price: parseFloat(form.purchasePrice) || 0,
    rehab_budget: parseFloat(form.rehabBudget) || 0,
    arv: form.arv ? parseFloat(form.arv) : null,
    rent_estimate_monthly: form.rentEstimate ? parseFloat(form.rentEstimate) : null,
    requested_loan_amount: form.requestedLoanAmount ? parseFloat(form.requestedLoanAmount) : null,
    closing_timeline_days: form.closingTimeline ? parseInt(form.closingTimeline) : null,
    exit_strategy: form.exitStrategy || null, notes: form.notes || null,
  });

  /* ── Submit (authenticated user) ── */
  const handleAuthSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/borrowers/intake`, buildPayload());
      setSubmitted(true);
      setDealResult(res.data);
      try { localStorage.removeItem(AUTOSAVE_KEY); } catch {}
      if (res.data.progression) {
        setDealProgress(res.data.progression);
      } else {
        setTimeout(async () => {
          try {
            const sr = await axios.get(`${API}/borrowers/intake/${res.data.lead_id}/status`);
            setDealProgress(sr.data);
          } catch {}
        }, 1500);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) setError(detail.map(d => d.msg || JSON.stringify(d)).join("; "));
      else if (typeof detail === "string") setError(detail);
      else setError("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ── Submit (new user — register + submit) ── */
  const handleRegisterAndSubmit = async () => {
    const pwdErrs = validatePassword(accountPassword);
    if (pwdErrs.length > 0) { setPasswordErrors(pwdErrs); return; }
    if (accountPassword !== accountConfirm) { setError("Passwords do not match."); return; }

    setLoading(true);
    setError(null);
    try {
      const payload = { ...buildPayload(), password: accountPassword };
      const res = await axios.post(`${API}/public/register-and-submit`, payload);

      // Store token and update auth context
      localStorage.setItem("ic_token", res.data.token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;

      // Login via context to update app state
      try {
        await login(form.email, accountPassword);
      } catch {
        // Token already set, proceed anyway
      }

      setSubmitted(true);
      setDealResult(res.data);
      try { localStorage.removeItem(AUTOSAVE_KEY); } catch {}
      if (res.data.progression) {
        setDealProgress(res.data.progression);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) setError(detail.map(d => d.msg || JSON.stringify(d)).join("; "));
      else if (typeof detail === "string") setError(detail);
      else setError("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoggedIn) await handleAuthSubmit();
    else await handleRegisterAndSubmit();
  };

  const handleDocUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || !dealResult?.lead_id) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(
        `${API}/borrowers/${dealResult.lead_id}/documents?category=${uploadCategory}`,
        formData, { headers: { "Content-Type": "multipart/form-data" } }
      );
      setUploadedDocs(prev => [...prev, { ...res.data, category_label: DOC_CATEGORIES.find(c => c.value === uploadCategory)?.label }]);
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }, [dealResult, uploadCategory]);

  /* ═══════════════════════════════════════════════════════════
     Step Progress Bar (5 steps)
     ═══════════════════════════════════════════════════════════ */
  const StepBar = () => {
    const timeRemaining = STEPS.slice(step - 1).map(s => s.time).join(" + ");
    return (
      <div className="mb-8 mx-auto max-w-3xl" data-testid="step-progress-bar">
        <div className="flex items-center justify-between relative mb-2">
          <div className="absolute top-4 left-0 right-0 h-0.5 bg-[#e8e0d0]" />
          <div className="absolute top-4 left-0 h-0.5 bg-emerald-500 transition-all duration-500" style={{ width: `${((step - 1) / (STEPS.length - 1)) * 100}%` }} />
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = step > i + 1;
            const active = step === i + 1;
            return (
              <div key={i} className="relative flex flex-col items-center" data-testid={`step-indicator-${i + 1}`}>
                <div className={`z-10 flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-all duration-300 ${
                  done ? "bg-emerald-500 text-white shadow-sm" : active ? "bg-[#022c22] text-[#e8d5b0] shadow-md ring-4 ring-emerald-100" : "bg-[#faf7f2] border-2 border-[#ddd] text-[#aaa]"
                }`}>{done ? <CheckCircle className="h-4 w-4" /> : <Icon className="h-3.5 w-3.5" />}</div>
                <span className={`mt-2 text-[10px] whitespace-nowrap transition-colors hidden sm:block ${active ? "text-[#0A0A0A] font-semibold" : done ? "text-emerald-600 font-medium" : "text-[#aaa]"}`}>{s.label}</span>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between mt-4 px-1">
          <div>
            <div className="text-sm font-semibold text-[#0A0A0A]">Step {step}: {STEPS[step - 1]?.label}</div>
            <div className="text-xs text-[#888] mt-0.5">{STEPS[step - 1]?.desc}</div>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-[#999]">
            <Clock className="h-3 w-3" />
            <span>Est. {timeRemaining} remaining</span>
          </div>
        </div>
        {lastSaved && (
          <div className="flex items-center gap-1 mt-2 text-[10px] text-[#bbb]">
            <Save className="h-2.5 w-2.5" /> Draft saved {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>
    );
  };

  /* ═══════════════════════════════════════════════════════════
     Trust Signals + Live Summary (Right Panel)
     ═══════════════════════════════════════════════════════════ */
  const RightPanel = ({ className = "" }) => (
    <div className={className}>
      <div className="sticky top-6 space-y-4">
        {/* Trust Signals */}
        <div className="rounded-2xl border border-[#e8e0d0] bg-white p-5 shadow-sm" data-testid="trust-signals-panel">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#888] mb-4">Your Information Is Safe</h3>
          <div className="space-y-4">
            {[
              { icon: Lock, title: "Bank-level encryption", desc: "256-bit SSL encryption on all data" },
              { icon: Shield, title: "Never shared without consent", desc: "Your information stays private until you approve" },
              { icon: Clock, title: "Under 10 minutes", desc: "Most borrowers complete this in under 10 minutes" },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-3">
                <div className="mt-0.5 h-8 w-8 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4 text-emerald-600" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#0A0A0A]">{title}</div>
                  <div className="text-xs text-[#888] mt-0.5">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Live Summary */}
        <div className="rounded-2xl border border-[#e8e0d0] bg-white p-5 shadow-sm" data-testid="live-summary-panel">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#888] mb-4">Request Summary</h3>
          <div className="space-y-3">
            {[
              { label: "Program", value: ltMap[form.loanType] || "Select a program", muted: !form.loanType },
              { label: "Property", value: form.propertyAddress || "Enter address", muted: !form.propertyAddress },
              { label: "Purchase Price", value: fmt$(form.purchasePrice), muted: !form.purchasePrice },
              ...(form.rehabBudget ? [{ label: "Rehab Budget", value: fmt$(form.rehabBudget) }] : []),
              { label: "Total Cost", value: fmt$(summary.totalCost || null), muted: !summary.totalCost },
              { label: "Est. Loan Amount", value: fmt$(summary.requested || null), highlight: true },
            ].map((item, i) => (
              <div key={item.label} className={`flex items-center justify-between py-1.5 ${i > 0 ? "border-t border-[#f5f0e8]" : ""}`}>
                <span className="text-xs text-[#888]">{item.label}</span>
                <span className={`text-sm font-semibold ${item.highlight ? "text-emerald-700" : item.muted ? "text-[#ccc]" : "text-[#0A0A0A]"}`}>{item.value}</span>
              </div>
            ))}
          </div>
          {(summary.ltc || summary.ltv) && (
            <div className="mt-4 pt-3 border-t border-[#e8e0d0] flex gap-3">
              {summary.ltc && (
                <div className="flex-1 rounded-lg bg-[#faf7f2] p-2.5 text-center">
                  <div className="text-[10px] font-bold uppercase text-[#888]">LTC</div>
                  <div className="text-lg font-bold text-[#0A0A0A]">{summary.ltc}%</div>
                </div>
              )}
              {summary.ltv && (
                <div className="flex-1 rounded-lg bg-[#faf7f2] p-2.5 text-center">
                  <div className="text-[10px] font-bold uppercase text-[#888]">LTV</div>
                  <div className="text-lg font-bold text-[#0A0A0A]">{summary.ltv}%</div>
                </div>
              )}
            </div>
          )}
        </div>

        <p className="text-[10px] leading-5 text-[#aaa] px-1">These figures are illustrative only and do not constitute an approval, commitment, or credit decision.</p>
      </div>
    </div>
  );

  /* ═══════════════════════════════════════════════════════════
     Confirmation Panel (Post-Submit)
     ═══════════════════════════════════════════════════════════ */
  const ConfirmationPanel = () => (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="submission-success">
      <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-emerald-700">Capital Request Submitted</h3>
            <p className="text-xs text-[#888]">Your scenario is now being processed by our AI underwriting team</p>
          </div>
        </div>
        {dealResult && (
          <div className="flex items-center gap-4">
            <div className="rounded-xl bg-white border border-emerald-200 px-5 py-3 shadow-sm">
              <div className="text-[10px] text-emerald-600 uppercase tracking-wider font-bold">Deal Number</div>
              <div className="text-xl font-bold text-[#0A0A0A] tracking-wide" data-testid="deal-number">{dealResult.deal_number}</div>
            </div>
            <div className="text-sm text-[#555] leading-relaxed">
              Track progress on your <button onClick={() => navigateTo("dashboard")} className="text-emerald-600 font-semibold underline underline-offset-2 hover:text-emerald-700 transition-colors" data-testid="go-to-dashboard">dashboard</button>.
            </div>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-[#e8e0d0] bg-white p-5">
        <h4 className="text-xs font-bold uppercase tracking-wider text-[#888] mb-4">What Happens Next</h4>
        <div className="space-y-0">
          {[
            { step: "AI Underwriting Review", desc: "Our AI agents analyze your deal for risk, viability, and market fit.", time: "~2 min" },
            { step: "Lender Matching", desc: "Your deal is matched with capital partners from our lender network.", time: "~1 min" },
            { step: "Deal Package Generation", desc: "A presentation-ready deal package is generated for qualified lenders.", time: "~1 min" },
            { step: "Lender Distribution", desc: "Matched lenders receive your anonymized deal package for review.", time: "24-48 hrs" },
            { step: "Term Sheet Collection", desc: "Competing term sheets are collected and presented for your selection.", time: "3-7 days" },
          ].map((item, i, arr) => (
            <div key={i} className="flex gap-4" data-testid={`next-step-${i}`}>
              <div className="flex flex-col items-center">
                <div className="flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold shrink-0 bg-[#faf7f2] border-2 border-[#ddd] text-[#999]">{i + 1}</div>
                {i < arr.length - 1 && <div className="w-0.5 flex-1 min-h-[24px] bg-[#e8e0d0]" />}
              </div>
              <div className="pb-5">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-[#0A0A0A]">{item.step}</span>
                  <span className="text-[10px] text-[#bbb] font-medium">{item.time}</span>
                </div>
                <p className="text-xs text-[#888] mt-0.5 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Document Upload */}
      {dealResult?.lead_id && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-5" data-testid="post-submit-doc-upload">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="h-4 w-4 text-emerald-600" />
            <h4 className="text-xs font-bold uppercase tracking-wider text-[#888]">Strengthen Your Submission</h4>
          </div>
          <p className="text-xs text-[#999] mb-4">Upload key documents now to reduce review time by 50% or more.</p>
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <select value={uploadCategory} onChange={e => setUploadCategory(e.target.value)}
              className={selectClass + " !w-auto !py-2.5 text-sm"} data-testid="doc-category-select">
              {DOC_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <label className="cursor-pointer" data-testid="upload-doc-btn">
              <input type="file" className="hidden" onChange={handleDocUpload}
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" disabled={uploading} />
              <span className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-emerald-200 bg-emerald-50 text-sm text-emerald-700 font-medium hover:bg-emerald-100 transition-all">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? "Uploading..." : "Upload Document"}
              </span>
            </label>
          </div>
          {uploadedDocs.length > 0 && (
            <div className="space-y-2">
              {uploadedDocs.map((doc, i) => (
                <div key={doc.doc_id || i} className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50/50 p-3">
                  <FileText className="h-4 w-4 text-emerald-600 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-[#0A0A0A] truncate" data-testid={`uploaded-doc-${i}`}>{doc.filename}</div>
                    <div className="text-[10px] text-[#888]">{doc.category_label}</div>
                  </div>
                  <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
                </div>
              ))}
            </div>
          )}
          {uploadedDocs.length === 0 && !uploading && (
            <div className="rounded-xl border-2 border-dashed border-[#e0d8ca] p-6 text-center">
              <Upload className="h-6 w-6 text-[#ccc] mx-auto mb-2" />
              <p className="text-xs text-[#aaa]">Drag files here or click Upload Document above</p>
              <p className="text-[10px] text-[#ccc] mt-1">PDF, JPG, PNG, DOC, XLS accepted</p>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-center">
        <GoldButton onClick={() => navigateTo("dashboard")} data-testid="dashboard-btn">
          Go to My Dashboard <ArrowRight className="h-4 w-4 inline ml-1" />
        </GoldButton>
      </div>
    </motion.div>
  );

  /* ═══════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════ */
  return (
    <div className="relative" data-testid="apply-page">
      <PageHero eyebrow="Capital Request" title="Start Your Capital Request"
        description="Walk through our guided intake to receive AI-powered underwriting, lender matching, and a presentation-ready deal package." />
      <div className="relative">
        <LuxuryBackground />
        <section className="relative z-10 mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
          {!submitted && <StepBar />}

          {/* Pre-fill banner from calculator */}
          {!submitted && urlPrefill && (
            <div className="mx-auto max-w-3xl mb-4 rounded-xl border border-teal-200 bg-teal-50/60 px-4 py-3 flex items-center gap-3" data-testid="calculator-prefill-banner">
              <Sparkles className="h-4 w-4 text-teal-600 shrink-0" />
              <p className="text-xs text-teal-800">
                <span className="font-semibold">We pre-filled your details from your deal analysis.</span> Review and continue.
              </p>
            </div>
          )}

          {/* Mobile summary toggle */}
          {!submitted && (
            <div className="lg:hidden mb-4">
              <button onClick={() => setShowMobileSummary(!showMobileSummary)}
                className="w-full flex items-center justify-between rounded-xl border border-[#e8e0d0] bg-white px-4 py-3 text-sm"
                data-testid="mobile-summary-toggle">
                <span className="font-semibold text-[#0A0A0A]">Request Summary</span>
                <div className="flex items-center gap-2">
                  {summary.requested > 0 && <span className="text-emerald-700 font-bold">{fmt$(summary.requested)}</span>}
                  {showMobileSummary ? <ChevronUp className="h-4 w-4 text-[#888]" /> : <ChevronDown className="h-4 w-4 text-[#888]" />}
                </div>
              </button>
              <AnimatePresence>
                {showMobileSummary && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <RightPanel className="pt-3" />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          <div className={`grid gap-6 ${submitted ? "" : "lg:grid-cols-[1.15fr_0.85fr]"}`}>
            {/* Main content */}
            <Card hover={false}>
              {submitted ? <ConfirmationPanel /> : (
                <form onSubmit={handleSubmit} data-testid="apply-form">
                  <AnimatePresence mode="wait">
                    {/* ── Step 1: Deal Details ── */}
                    {step === 1 && (
                      <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6" data-testid="step-1-deal-details">
                        <FieldGroup title="Deal Overview">
                          <div className="grid gap-4 md:grid-cols-2">
                            <label className="block md:col-span-2">
                              <span className={labelClass}>Loan Type *</span>
                              <select value={form.loanType} onChange={e => handleChange("loanType", e.target.value)}
                                className={selectClass} data-testid="input-loanType" required>
                                <option value="">Select a loan program</option>
                                {LOAN_TYPES.map(lt => <option key={lt.value} value={lt.value}>{lt.label}</option>)}
                              </select>
                            </label>
                            <label className="block md:col-span-2">
                              <span className={labelClass}>Property Address *</span>
                              <div className="relative">
                                <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#999]" />
                                <input required value={form.propertyAddress} onChange={e => handleChange("propertyAddress", e.target.value)}
                                  className={inputClass + " pl-10"} placeholder="123 Main St, Houston TX 77001" data-testid="input-propertyAddress" />
                              </div>
                            </label>
                            <label className="block">
                              <span className={labelClass}>Loan Amount Requested <OptionalBadge /></span>
                              <div className="relative">
                                <DollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#999]" />
                                <input type="number" value={form.requestedLoanAmount} onChange={e => handleChange("requestedLoanAmount", e.target.value)}
                                  className={inputClass + " pl-10"} placeholder="Auto-calculated if left blank" data-testid="input-requestedLoanAmount" />
                              </div>
                            </label>
                            <label className="block">
                              <span className={labelClass}>Intended Close Date (days) <OptionalBadge /></span>
                              <div className="relative">
                                <Clock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#999]" />
                                <input type="number" min={7} max={365} value={form.closingTimeline} onChange={e => handleChange("closingTimeline", e.target.value)}
                                  className={inputClass + " pl-10"} placeholder="21" data-testid="input-closingTimeline" />
                              </div>
                            </label>
                          </div>
                        </FieldGroup>
                      </motion.div>
                    )}

                    {/* ── Step 2: Property Info ── */}
                    {step === 2 && (
                      <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6" data-testid="step-2-property-info">
                        <FieldGroup title="Property Type">
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                            {PROPERTY_TYPES.map(pt => {
                              const Icon = pt.icon;
                              const selected = form.propertyType === pt.value;
                              return (
                                <button key={pt.value} type="button" onClick={() => handleChange("propertyType", pt.value)}
                                  data-testid={`property-type-${pt.value}`}
                                  className={`rounded-xl border p-3.5 text-left transition-all ${
                                    selected ? "border-emerald-500 bg-emerald-50 shadow-sm ring-1 ring-emerald-200" : "border-[#e8e0d0] bg-white hover:border-emerald-300 hover:bg-[#faf7f2]"
                                  }`}>
                                  <div className="flex items-center gap-2 mb-1">
                                    <Icon className={`h-4 w-4 ${selected ? "text-emerald-600" : "text-[#bbb]"}`} />
                                    <span className={`text-sm font-semibold ${selected ? "text-emerald-800" : "text-[#0A0A0A]"}`}>{pt.label}</span>
                                  </div>
                                  <div className="text-[10px] text-[#999] leading-snug">{pt.desc}</div>
                                </button>
                              );
                            })}
                          </div>
                        </FieldGroup>

                        {showResidentialFields && !isLand && (
                          <FieldGroup title="Property Details">
                            <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
                              <label className="block"><span className={labelClass}>Bedrooms <OptionalBadge /></span>
                                <input type="number" value={form.bedrooms} onChange={e => handleChange("bedrooms", e.target.value)}
                                  className={inputClass} placeholder="3" data-testid="input-bedrooms" /></label>
                              <label className="block"><span className={labelClass}>Bathrooms <OptionalBadge /></span>
                                <input type="number" step="0.5" value={form.bathrooms} onChange={e => handleChange("bathrooms", e.target.value)}
                                  className={inputClass} placeholder="2" data-testid="input-bathrooms" /></label>
                              <label className="block"><span className={labelClass}>Sq Ft <OptionalBadge /></span>
                                <input type="number" value={form.sqft} onChange={e => handleChange("sqft", e.target.value)}
                                  className={inputClass} placeholder="1,800" data-testid="input-sqft" /></label>
                              <label className="block"><span className={labelClass}>Year Built <OptionalBadge /></span>
                                <input type="number" value={form.yearBuilt} onChange={e => handleChange("yearBuilt", e.target.value)}
                                  className={inputClass} placeholder="1985" data-testid="input-yearBuilt" /></label>
                            </div>
                          </FieldGroup>
                        )}

                        <FieldGroup title="Deal Economics">
                          <div className="grid gap-4 md:grid-cols-2">
                            <label className="block"><span className={labelClass}>Purchase Price *</span>
                              <div className="relative">
                                <DollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#999]" />
                                <input required type="number" value={form.purchasePrice} onChange={e => handleChange("purchasePrice", e.target.value)}
                                  className={inputClass + " pl-10"} placeholder="300,000" data-testid="input-purchasePrice" />
                              </div>
                            </label>
                            {showRehab && (
                              <label className="block">
                                <span className={labelClass}>Rehab Budget <OptionalBadge />
                                  <Tooltip text="Total estimated renovation cost including materials, labor, and contingency." />
                                </span>
                                <div className="relative">
                                  <DollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#999]" />
                                  <input type="number" value={form.rehabBudget} onChange={e => handleChange("rehabBudget", e.target.value)}
                                    className={inputClass + " pl-10"} placeholder="50,000" data-testid="input-rehabBudget" />
                                </div>
                              </label>
                            )}
                            {showARV && (
                              <label className="block">
                                <span className={labelClass}>After Repair Value (ARV) <OptionalBadge />
                                  <Tooltip text="Estimated market value after all renovations are complete." />
                                </span>
                                <div className="relative">
                                  <DollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#999]" />
                                  <input type="number" value={form.arv} onChange={e => handleChange("arv", e.target.value)}
                                    className={inputClass + " pl-10"} placeholder="450,000" data-testid="input-arv" />
                                </div>
                              </label>
                            )}
                            {showRent && (
                              <label className="block"><span className={labelClass}>Monthly Rent Estimate *
                                <Tooltip text="Gross monthly rental income expected from the property." />
                              </span>
                                <div className="relative">
                                  <DollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#999]" />
                                  <input type="number" value={form.rentEstimate} onChange={e => handleChange("rentEstimate", e.target.value)}
                                    className={inputClass + " pl-10"} placeholder="2,500" data-testid="input-rentEstimate" />
                                </div>
                              </label>
                            )}
                            {!showRehab && !showRent && !showARV && (
                              <label className="block">
                                <span className={labelClass}>ARV / Stabilized Value <OptionalBadge /></span>
                                <div className="relative">
                                  <DollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#999]" />
                                  <input type="number" value={form.arv} onChange={e => handleChange("arv", e.target.value)}
                                    className={inputClass + " pl-10"} placeholder="450,000" data-testid="input-arv" />
                                </div>
                              </label>
                            )}
                          </div>
                        </FieldGroup>

                        <FieldGroup title="Exit Strategy">
                          <div className="grid grid-cols-2 gap-2">
                            {(EXIT_BY_LOAN_TYPE[form.loanType] ? EXIT_STRATEGIES.filter(es => EXIT_BY_LOAN_TYPE[form.loanType].includes(es.value)) : EXIT_STRATEGIES).map(es => {
                              const selected = form.exitStrategy === es.value;
                              return (
                                <button key={es.value} type="button" onClick={() => handleChange("exitStrategy", es.value)}
                                  data-testid={`exit-${es.value}`}
                                  className={`rounded-xl border p-3 text-left transition-all ${
                                    selected ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-200" : "border-[#e8e0d0] bg-white hover:border-emerald-300"
                                  }`}>
                                  <div className={`text-xs font-semibold ${selected ? "text-emerald-800" : "text-[#0A0A0A]"}`}>{es.label}</div>
                                  <div className="text-[10px] text-[#999] mt-0.5">{es.desc}</div>
                                </button>
                              );
                            })}
                          </div>
                        </FieldGroup>
                      </motion.div>
                    )}

                    {/* ── Step 3: Borrower Profile ── */}
                    {step === 3 && (
                      <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6" data-testid="step-3-borrower-profile">
                        <FieldGroup title="Contact Information">
                          <div className="grid gap-4 md:grid-cols-2">
                            <label className="block"><span className={labelClass}>Full Name *</span>
                              <input required value={form.fullName} onChange={e => handleChange("fullName", e.target.value)}
                                className={inputClass} placeholder="Your full legal name" data-testid="input-fullName" /></label>
                            <label className="block"><span className={labelClass}>Email *</span>
                              <input required type="email" value={form.email} onChange={e => handleChange("email", e.target.value)}
                                className={inputClass} placeholder="you@email.com" data-testid="input-email" /></label>
                            <label className="block"><span className={labelClass}>Phone *</span>
                              <input required value={form.phone} onChange={e => handleChange("phone", e.target.value)}
                                className={inputClass} placeholder="(832) 555-1234" data-testid="input-phone" /></label>
                            <label className="block"><span className={labelClass}>State</span>
                              <select value={form.state} onChange={e => handleChange("state", e.target.value)}
                                className={selectClass} data-testid="select-state">
                                {["TX","FL","GA","AZ","NC","SC","TN","CA","CO","NY","NJ","OH","PA","IL","VA","MD"].map(st =>
                                  <option key={st} value={st}>{st}</option>)}
                              </select></label>
                          </div>
                        </FieldGroup>
                        <FieldGroup title="Borrower Background">
                          <div className="grid gap-4 md:grid-cols-3">
                            <label className="block"><span className={labelClass}>Entity Name <OptionalBadge /></span>
                              <input value={form.entityName} onChange={e => handleChange("entityName", e.target.value)}
                                className={inputClass} placeholder="LLC / Corp name" data-testid="input-entityName" /></label>
                            <label className="block"><span className={labelClass}>Projects Completed <OptionalBadge /></span>
                              <input type="number" value={form.experienceProjects} onChange={e => handleChange("experienceProjects", e.target.value)}
                                className={inputClass} placeholder="0" data-testid="input-experience" /></label>
                            <label className="block"><span className={labelClass}>Estimated FICO <OptionalBadge /></span>
                              <input type="number" value={form.ficoEstimate} onChange={e => handleChange("ficoEstimate", e.target.value)}
                                className={inputClass} placeholder="680" data-testid="input-fico" /></label>
                          </div>
                        </FieldGroup>
                      </motion.div>
                    )}

                    {/* ── Step 4: Documents & Notes ── */}
                    {step === 4 && (
                      <motion.div key="step4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6" data-testid="step-4-documents">
                        <FieldGroup title="Additional Notes">
                          <label className="block"><span className={labelClass}>Notes for the Review Team <OptionalBadge /></span>
                            <textarea value={form.notes} onChange={e => handleChange("notes", e.target.value)} rows={3}
                              className={inputClass + " resize-none"} placeholder="Any additional context about the deal, sponsor, or special circumstances..." data-testid="textarea-notes" /></label>
                        </FieldGroup>

                        <FieldGroup title="Recommended Documents">
                          <p className="text-xs text-[#888] -mt-2 mb-3">You will be able to upload documents right after submission. Having these ready will speed up your review.</p>
                          <div className="space-y-2.5">
                            {[
                              { label: "Purchase Agreement / Contract", desc: "Signed purchase contract for the subject property" },
                              { label: "Property Photos", desc: "At least 5 photos showing current condition" },
                              { label: "Renovation Plan / Scope of Work", desc: "Detailed breakdown of planned improvements" },
                              { label: "Bank Statements", desc: "Last 2-3 months showing proof of funds" },
                            ].map(doc => (
                              <div key={doc.label} className="flex items-start gap-3 rounded-xl border border-[#e8e0d0] bg-[#faf7f2] p-3.5">
                                <FileText className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                                <div>
                                  <div className="text-sm font-medium text-[#0A0A0A]">{doc.label}</div>
                                  <div className="text-[10px] text-[#888] mt-0.5">{doc.desc}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </FieldGroup>
                      </motion.div>
                    )}

                    {/* ── Step 5: Account Creation & Submit (or Review & Submit if logged in) ── */}
                    {step === 5 && (
                      <motion.div key="step5" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6" data-testid="step-5-submit">
                        {isLoggedIn ? (
                          /* Logged in: Review summary + Submit */
                          <>
                            <div className="flex items-center gap-2 mb-1">
                              <ClipboardCheck className="h-5 w-5 text-emerald-600" />
                              <h3 className="text-base font-bold text-[#0A0A0A]">Review Your Capital Request</h3>
                            </div>
                            <p className="text-xs text-[#888] -mt-3">Please confirm all details before submitting. You can go back to edit any section.</p>
                            {[
                              { title: "Deal Details", stepNum: 1, items: [
                                { label: "Loan Program", value: ltMap[form.loanType] || "--" },
                                { label: "Property", value: form.propertyAddress || "--" },
                                ...(form.requestedLoanAmount ? [{ label: "Requested Amount", value: fmt$(form.requestedLoanAmount) }] : []),
                                ...(form.closingTimeline ? [{ label: "Close Timeline", value: `${form.closingTimeline} days` }] : []),
                              ]},
                              { title: "Property & Financials", stepNum: 2, items: [
                                { label: "Type", value: PROPERTY_TYPES.find(t => t.value === form.propertyType)?.label || "--" },
                                { label: "Purchase Price", value: fmt$(form.purchasePrice) },
                                ...(form.rehabBudget ? [{ label: "Rehab", value: fmt$(form.rehabBudget) }] : []),
                                ...(form.arv ? [{ label: "ARV", value: fmt$(form.arv) }] : []),
                                { label: "Est. Loan", value: fmt$(summary.requested), highlight: true },
                              ]},
                              { title: "Borrower", stepNum: 3, items: [
                                { label: "Name", value: form.fullName },
                                { label: "Email", value: form.email },
                                { label: "Phone", value: form.phone },
                                { label: "FICO", value: form.ficoEstimate },
                              ]},
                            ].map(section => (
                              <div key={section.title} className="rounded-xl border border-[#e8e0d0] bg-white overflow-hidden">
                                <div className="flex items-center justify-between px-4 py-3 bg-[#faf7f2] border-b border-[#e8e0d0]">
                                  <h4 className="text-xs font-bold uppercase tracking-wider text-[#888]">{section.title}</h4>
                                  <button type="button" onClick={() => setStep(section.stepNum)}
                                    className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 font-medium transition-colors"
                                    data-testid={`review-edit-step-${section.stepNum}`}>
                                    <Pencil className="h-3 w-3" /> Edit
                                  </button>
                                </div>
                                <div className="p-4 grid grid-cols-2 gap-x-4 gap-y-2.5">
                                  {section.items.map(item => (
                                    <div key={item.label}>
                                      <div className="text-[10px] text-[#999] uppercase font-bold">{item.label}</div>
                                      <div className={`text-sm mt-0.5 truncate ${item.highlight ? "font-bold text-emerald-700" : "font-medium text-[#0A0A0A]"}`}>{item.value || "--"}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </>
                        ) : (
                          /* Not logged in: Account creation */
                          <>
                            <div className="flex items-center gap-2 mb-1">
                              <Shield className="h-5 w-5 text-emerald-600" />
                              <h3 className="text-base font-bold text-[#0A0A0A]">Almost Done</h3>
                            </div>
                            <p className="text-sm text-[#555] -mt-3 leading-relaxed">
                              Create your free account to submit your deal and start receiving term sheets.
                            </p>
                            <div className="grid gap-4">
                              <label className="block"><span className={labelClass}>Email</span>
                                <input type="email" value={form.email} readOnly
                                  className={inputClass + " bg-[#faf7f2] text-[#888]"} data-testid="account-email" />
                                <p className="mt-1 text-[10px] text-[#999]">From your borrower profile (Step 3)</p>
                              </label>
                              <label className="block"><span className={labelClass}>Password *</span>
                                <div className="relative">
                                  <input type={showPassword ? "text" : "password"} value={accountPassword}
                                    onChange={e => { setAccountPassword(e.target.value); setPasswordErrors(validatePassword(e.target.value)); }}
                                    className={inputClass + " pr-10"} placeholder="Min 8 chars, uppercase, lowercase, digit" data-testid="account-password" />
                                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999] hover:text-[#555]">
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                  </button>
                                </div>
                                {passwordErrors.length > 0 && accountPassword && (
                                  <div className="mt-1.5 space-y-1">
                                    {passwordErrors.map(err => (
                                      <div key={err} className="text-[10px] text-red-500 flex items-center gap-1">
                                        <AlertCircle className="h-2.5 w-2.5" /> {err}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </label>
                              <label className="block"><span className={labelClass}>Confirm Password *</span>
                                <input type={showPassword ? "text" : "password"} value={accountConfirm}
                                  onChange={e => setAccountConfirm(e.target.value)}
                                  className={inputClass} placeholder="Re-enter your password" data-testid="account-confirm-password" />
                                {accountConfirm && accountPassword !== accountConfirm && (
                                  <p className="mt-1 text-[10px] text-red-500 flex items-center gap-1">
                                    <AlertCircle className="h-2.5 w-2.5" /> Passwords do not match
                                  </p>
                                )}
                              </label>
                            </div>
                          </>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {error && (
                    <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600 flex items-center gap-2" data-testid="form-error">
                      <AlertCircle className="h-5 w-5 shrink-0" />{error}
                    </div>
                  )}

                  {/* Navigation */}
                  <div className="flex items-center justify-between mt-8 pt-5 border-t border-[#e8e0d0]">
                    {step > 1 ? (
                      <button type="button" onClick={prevStep}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-[#e8e0d0] text-sm font-medium text-[#555] hover:bg-[#faf7f2] transition-colors"
                        data-testid="prev-step-btn">
                        <ArrowLeft className="h-4 w-4" /> Back
                      </button>
                    ) : <div />}
                    {step < 5 ? (
                      <GoldButton type="button" onClick={nextStep} disabled={!validateStep(step)} data-testid="next-step-btn">
                        Continue <ArrowRight className="h-4 w-4 inline ml-1" />
                      </GoldButton>
                    ) : (
                      <GoldButton type="submit" disabled={loading || !validateStep(5)} data-testid="submit-application">
                        {loading ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Processing...</span> : (
                          <span className="flex items-center gap-2">
                            <Shield className="h-4 w-4" />
                            {isLoggedIn ? "Submit Capital Request" : "Create Account & Submit"}
                          </span>
                        )}
                      </GoldButton>
                    )}
                  </div>
                </form>
              )}
            </Card>

            {/* Right panel: Trust Signals + Summary (desktop) */}
            {!submitted && <RightPanel className="hidden lg:block" />}

            {/* Post-submit: standalone, no right panel */}
          </div>
        </section>
      </div>
    </div>
  );
}
